import React, { Component } from 'react'
import { connect } from 'react-redux'
import { withI18n, withI18nProps } from '@lingui/react'
import compose from 'lodash/fp/compose'
import {
  getBookById,
  getBooksByIds,
} from 'client/bookmate/selectors/book-selector'

import { getNonExpiredAccessLevels } from 'client/bookmate/selectors/subscription-selectors'

import {
  loadReaders,
  loadRelated,
  loadQuotes,
  loadImpressions,
  loadShelves,
  loadPublisherResources,
} from 'client/bookmate/reducers/serial-reducer'

import {
  getImpressionsByIds,
  loadCurrentUserImpression,
  IMPRESSION_CURRENT_USER_CLEAN,
} from 'client/bookmate/reducers/impressions-reducer'

import {
  loadEmotions,
  getAllEmotions,
} from 'client/bookmate/reducers/emotions-reducer'

import prepareComponent from 'client/shared/decorators/prepare-component'
import errorBoundary from 'client/shared/decorators/error-boundary'

import Spacer from 'client/shared/blocks/spacer'
import { LayoutColumn } from 'client/shared/blocks/layout-column'

import { PromoGifts } from 'client/bookmate/blocks/promo-gifts'

import Footer from 'client/shared/blocks/footer'
import HeaderBox from 'client/shared/boxes/header-box'
import SerialInfo from './serial-info'

import withMarketing, {
  MarketingProps,
} from 'client/shared/decorators/with-marketing'
import { SerialProps } from 'client/shared/types/serial'
import { QuoteProps } from 'client/shared/types/quote'
import { ImpressionProps } from 'client/shared/types/impression'
import { Dispatch, State } from 'shared/types/redux'
import { Location, Params } from 'client/shared/types/react-router'
import { EmotionRatingProps, EmotionProps } from 'client/shared/types/emotion'
import { State as AppState } from 'client/shared/reducers/app-reducer'
import { markSerialAsSeen } from 'client/bookmate/reducers/serial-reducer'

type Props = {
  app: AppState
  auth: boolean
  children: React.ReactNode
  serial: SerialProps
  quotesIndex: QuoteProps[]
  impressionsIndex: ImpressionProps[]
  dispatch: Dispatch
  location: Location
  params: Params
  emotionRating: EmotionRatingProps[]
  emotions: EmotionProps[]
  hasCurrentUserImpression: boolean
} & MarketingProps &
  withI18nProps

class SerialSectionBox extends Component<Props> {
  componentDidMount() {
    const {
      serial: { uuid, library_card },
      dispatch,
    } = this.props

    if (library_card && library_card.has_new_episodes) {
      dispatch(markSerialAsSeen(uuid))
    }
  }

  render() {
    const {
      children,
      serial,
      location: { query, pathname },
      emotionRating,
      episodes,
      userActiveAccessLevels,
    } = this.props

    return (
      <>
        <HeaderBox />
        <SerialInfo
          serial={serial}
          episodes={episodes}
          emotionRating={emotionRating}
          fromSeries={query.from_series}
          userActiveAccessLevels={userActiveAccessLevels}
          currentPage={pathname}
        />
        <div className="resource-tab__active">
          <LayoutColumn>
            {children}
            <Spacer size={24} />
            <PromoGifts query={query} />
          </LayoutColumn>
        </div>
        <Footer withNotice />
      </>
    )
  }
}

const prepareWrapper = prepareComponent(
  async function ({ store, params }) {
    const { uuid } = params
    const { dispatch } = store
    const { currentUser } = store.getState()

    await dispatch({ type: IMPRESSION_CURRENT_USER_CLEAN })

    const load = [
      currentUser.auth &&
        dispatch(
          loadCurrentUserImpression({
            resourceUuid: uuid,
            resourceType: 'book',
          }),
        ),
      dispatch(loadQuotes({ uuid, p: 1, pp: 3, index: true })),
      dispatch(loadPublisherResources(uuid)),
      dispatch(loadImpressions({ uuid, p: 1, pp: 3, index: true })),
      dispatch(loadShelves({ uuid, p: 1, pp: 5 })),
      dispatch(loadRelated({ uuid, p: 1, pp: 5 })),
      dispatch(loadReaders({ uuid, p: 1, pp: 5 })),
      dispatch(loadEmotions()),
    ].filter(Boolean)
    await Promise.all(load)
  },
  {
    userListener: true,
    updateParams: 'uuid',
  },
)

const connectWrapper = connect((state: State) => ({
  auth: state.currentUser.auth,
  serial: getBookById(state, state.serial.uuid),
  episodes: getBooksByIds(state, state.serial.episodes),
  userActiveAccessLevels: getNonExpiredAccessLevels(state),
  impressionsIndex: getImpressionsByIds(state, state.serial.impressionsIndex),
  emotionRating: state.serial.emotionRating,
  emotions: getAllEmotions(state),
  hasCurrentUserImpression: Boolean(state.serial.currentUserImpressionUuid),
}))

const wrappers = compose(
  prepareWrapper,
  connectWrapper,
  errorBoundary,
  withI18n({ update: true }),
  withMarketing,
)

export default wrappers(SerialSectionBox)
